import { useAppTFunction } from '@retail/app/i18n';
import { useToast } from '@retail/hooks';
import {
  useMutateAddItemsToAssortment,
  useMutateExpireAssortmentItems,
} from '@retail/my-assortment/data-access';
import {
  AssortmentItemPriorityDTO,
  AssortmentPricesDTO,
  AssortmentWithCountDTO,
  CentralAssortmentItemDTO,
  MutateItemDTO,
} from '@retail/my-assortment/types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { ItemPriority } from '@retail/products/types';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { format } from 'date-fns';

export interface AssortmentActionOptions {
  skipToast?: boolean;
}

export interface ExpireItemsOptions {
  assortmentId?: number;
  supplierItemIds: number[];
  validTo?: string;
}

export const useAssortmentItemsActions = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const t = useAppTFunction();
  const { assortmentId } = useSelectedOrgUnit();

  const { mutateAsync: mutateUpdateItems, isPending: updateAssortmentItemsIsLoading } =
    useMutateAddItemsToAssortment({});

  const { mutateAsync: mutateExpireItems, isPending: expireItemsIsLoading } =
    useMutateExpireAssortmentItems();

  const addItemsToLocationAndCustomerAssortments = useCallback(
    async (
      itemsToAdd: AssortmentItemPriorityDTO[],
      customerAssortmentIds: number[],
      { skipToast }: AssortmentActionOptions = {}
    ) => {
      const locationItems: MutateItemDTO[] = itemsToAdd.map((item) => ({
        mgSupplierId: item.mgSupplierId,
        assortmentId,
        gtinCode: item.gtinCode,
        mgItemNumber: item.mgItemNumber,
        assortmentTags: item.assortmentTags,
        validFrom: item.validFrom,
        validTo: item.validTo,
        priority: item.priority ?? ItemPriority.ZERO,
        isStocked: item.isStocked,
      }));
      const customerItems: MutateItemDTO[] = customerAssortmentIds.flatMap((customerAssortmentId) =>
        locationItems.map((item) => ({
          ...item,
          assortmentId: customerAssortmentId,
          assortmentTags: undefined,
        }))
      );

      try {
        await mutateUpdateItems({
          body: [...locationItems, ...customerItems],
        });

        if (!skipToast) {
          toast.success(
            t(
              itemsToAdd.length === 1
                ? 'products.mgItem.addItemsToAssortment.itemAdded'
                : 'products.mgItem.addItemsToAssortment.itemsAdded',
              { nrOfItems: itemsToAdd.length }
            )
          );
        }
        queryClient.invalidateQueries({ queryKey: ['assortmentItems'] });
        queryClient.invalidateQueries({ queryKey: ['suppliers'] });
        queryClient.invalidateQueries({ queryKey: ['central-assortments'] });
        queryClient.invalidateQueries({ queryKey: ['assortment-prices'] });
        queryClient.invalidateQueries({ queryKey: ['mg-item-assortment-tags'] });
      } catch (error) {
        console.error(error);
        if (!skipToast) toast.error(t('common.genericError'));
        throw error;
      }
    },
    [assortmentId, mutateUpdateItems, toast, t, queryClient]
  );

  const addItemsToAssortment = useCallback(
    (itemsToAdd: AssortmentItemPriorityDTO[], options?: AssortmentActionOptions) =>
      addItemsToLocationAndCustomerAssortments(itemsToAdd, [], options),
    [addItemsToLocationAndCustomerAssortments]
  );

  /**
   * Drops the expired items from the cached lists so the rows disappear as soon as the
   * request resolves, instead of only once the refetch comes back. The refetch that
   * follows is what confirms it.
   */
  const removeItemsFromCachedLists = useCallback(
    (supplierItemIds: number[]) => {
      const expiredIds = new Set(supplierItemIds);

      const prune = <T extends { mgSupplierItemId: number }>(
        cached?: AssortmentWithCountDTO<T>
      ) => {
        if (!cached) return cached;

        const items = cached.items.filter((item) => !expiredIds.has(item.mgSupplierItemId));
        const removed = cached.items.length - items.length;
        if (!removed) return cached;

        return {
          ...cached,
          items,
          totalNumberOfItems: cached.totalNumberOfItems - removed,
          foundNumberOfItems: cached.foundNumberOfItems - removed,
        };
      };

      queryClient.setQueriesData<AssortmentWithCountDTO<AssortmentPricesDTO>>(
        { queryKey: ['assortment-prices'] },
        prune
      );
      queryClient.setQueriesData<AssortmentWithCountDTO<CentralAssortmentItemDTO>>(
        { queryKey: ['central-assortments'] },
        prune
      );
    },
    [queryClient]
  );

  const expireItems = useCallback(
    async ({
      supplierItemIds,
      validTo,
      assortmentId: expireInAssortmentId = assortmentId,
    }: ExpireItemsOptions) => {
      try {
        await mutateExpireItems({
          body: { supplierItemIds, validTo },
          urlVariables: { assortmentId: expireInAssortmentId },
        });

        if (!validTo || validTo.slice(0, 10) <= format(new Date(), 'yyyy-MM-dd')) {
          removeItemsFromCachedLists(supplierItemIds);
        }

        queryClient.invalidateQueries({ queryKey: ['assortmentItems'] });
        queryClient.invalidateQueries({ queryKey: ['central-assortments'] });
        queryClient.invalidateQueries({ queryKey: ['assortment-prices'] });
        toast.success(t('assortment.deleteItems.success'));
      } catch (error) {
        console.error(error);
        toast.error(t('common.genericError'));
        throw error;
      }
    },
    [mutateExpireItems, queryClient, assortmentId, removeItemsFromCachedLists, t, toast]
  );

  return {
    expireItems,
    addItemsToAssortment,
    addItemsToLocationAndCustomerAssortments,
    updateItemsIsLoading: updateAssortmentItemsIsLoading,
    expireItemsIsLoading,
  };
};
